import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import {
    Box, 
    Container,
    Typography,
    TextField,
    InputAdornment,
    Button,
    Drawer,
    CircularProgress,
    Card,
    CardContent,
    Stack,
    useTheme,
    useMediaQuery,
    Pagination,
    MenuItem,
    Select,
    DialogTitle,
    DialogContent,
    Dialog,
    DialogActions,
    Tooltip,
    Tabs,
    Tab,
    Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SearchIcon from '@mui/icons-material/Search';
import StoreIcon from '@mui/icons-material/Store';
import { useAuth } from '../contexts/AuthContext';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { saveAs } from 'file-saver';
import VehicleDialog from './vehicleDialog';
import ServiceDialog from './ServiceDialog';
import ProvidersDialog from './ProvidersDialog';
import ReportsMenu from './ReportsMenu';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import '../styles/fleet-dashboard.css';
import BulkArchiveDialog from './BulkArchiveDialog';
import U10RemindersPanel from './U10RemindersPanel';

/**
 * Formats an ISO date string as MM/DD/YYYY
 * Returns an empty string for falsy input or invalid dates.
 * 
 * @param {string} iso 
 * @returns {string}
 */
const formatDateMDY = (iso) => {
    if (!iso) {
        return '';
    }
    const d = new Date(iso);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
};

/**
 * Escapes a string so it can be safely used inside a RegExp pattern.
 * 
 * @param {string} [s='']  
 * @returns {string}
 */
const escRe = (s = '') => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Returns the vehicle comments with an "Archived <date>" suffix when applicable.
 * Avoids duplicating the archived marker if it already exists in the comment text.
 * @param {Object} row 
 * @returns {string}
 */
const displayComments = (row) => {
  const base = (row?.Comments || '').trim();
  const isArchived = !!row?.IsArchived;
  const at = row?.ArchivedAt;
  if (!isArchived || !at) return base;

  const archivedDate = formatDateMDY(at);      
  const archivedFrag = `Archived ${archivedDate}`;

  const hasArchived = new RegExp(`\\bArchived(?:\\s+on)?\\s+${escRe(archivedDate)}\\b`, 'i').test(base);
  if (hasArchived) return base;

  return base ? `${base} - ${archivedFrag}` : archivedFrag;
};

const COLUMNS = [
    { field: 'Make', headerName: 'Make', flex: 1 },
    { 
        field: 'Model', 
        headerName: 'Model', 
        flex: 1
    },
    { field: 'Year', headerName: 'Year', flex: 0.6 },
    { field: 'License', headerName: 'License Plate', flex: 1 },
    { field: 'VIN', headerName: 'VIN', flex: 2, minWidth: 150 },
    { field: 'OtherId', headerName: 'AHP', flex: 0.6 },
    { 
        field: 'Comments', 
        headerName: 'Comments', 
        flex: 2, 
        minWidth: 250,
        valueGetter: (params) => displayComments(params.row),
    },
];

const thisYear = new Date().getFullYear();

/**
 * Checks if a string is a vehicle year between 1980 and next year.
 * 
 * @param {string} s
 * @returns {boolean}
 */
const isYear = (s) => /^\d{4}$/.test(s) && +s >= 1980 && +s <= thisYear + 1;

const API_BASE = process.env.REACT_APP_API_BASE || '';

/**
 * FleetDashboard is the main fleet management screen.
 * 
 * It handles vehicle search, CRUD operations, service history, and
 * archiving/unarchiving across desktop and mobile layouts.
 * 
 * @returns {JSX.Element}
 */
const FleetDashboard = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [noteViewer, setNoteViewer] = useState({ open: false, text: '' });
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [cardPage, setCardPage] = useState(1);
    const [cardPageSize, setCardPageSize] = useState(10);
    const [archivedFilter, setArchivedFilter] = useState('0');
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [archiveInit, setArchiveInit] = useState(null);
    const [providersOpen, setProvidersOpen] = useState(false);
    const [rowSelectionModel, setRowSelectionModel] = useState([]);
    const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);
    const selectedCount = rowSelectionModel.length;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { token, role, loadingUser, logout, user } = useAuth();

    const isAdmin = role === 'admin';
    const [dlgOpen, setDlgOpen] = useState(false);
    const [dlgInitial, setDlgInitial] = useState(null);
    const [serviceDlgOpen, setServiceDlgOpen] = useState(false);
    const [serviceDlgInit, setServiceDlgInit] = useState(null);
    const [confirm, setConfirm] = useState({
        open: false,
        title: '',
        content: '',
        loading: false,
        onConfirm: null, 
    });

    const clamp2 = {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
    };

    const baseHistoryCols = [
        { 
            field: 'ServiceDate', 
            headerName: 'Service Date', 
            flex: 1,
            valueGetter: (params) => params.value ? params.value.split('T')[0] : ''
        },
        { 
            field: 'Description', 
            headerName: 'Description', 
            flex: 2, 
            valueGetter: (params) => params.row.Description ?? ''
        },
        { field: 'Mileage', headerName: 'Mileage (km)', type: 'number', flex: 1 },
        { field: 'Provider', headerName: 'Provider', flex: 1.2 },
        { field: 'TotalCost', headerName: 'Total ($)', type: 'number', flex: 0.8 },
        { 
            field: 'Notes', 
            headerName: 'Notes', 
            flex: 2,
            valueGetter: (params) => params.row.Notes ?? '',
            renderCell: (params) => {
                const text = params.value || '';
                const isLong = text.length > 50;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Box title={text} sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={clamp2}>
                                {text}
                            </Typography>
                        </Box>

                        {isLong && (
                            <Button
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setNoteViewer({ open: true, text });
                                }}
                            >
                                More
                            </Button>
                        )}
                    </Box>
                )
            }
        }
    ];

    const actionsCol = {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 120,
            getActions: (params) => [
                <GridActionsCellItem
                    key="edit"
                    icon={<EditIcon />}
                    label="Edit"
                    onClick={(e) => {
                        e.stopPropagation();
                        setServiceDlgInit(params.row);
                        setServiceDlgOpen(true);
                    }}
                    showInMenu={false}
                />,
                <GridActionsCellItem
                    key="delete"
                    icon={<DeleteIcon />}
                    label="Delete"
                    onClick={(e) => {
                        e.stopPropagation();
                        confirmDeleteService(params.row);
                    }}
                    showInMenu={false}
                    sx={{ color: 'error.main' }}
                />,
            ],
    }

    const historyCols = isAdmin ? [...baseHistoryCols, actionsCol] : baseHistoryCols;

    /**
     * Parses a free-text vehicle search query into a year and remaining word tokens.
     * 
     * Used to support queries like "2020 toyota camry" where the year is treated specially.
     * 
     * @param {string} q
     * @returns {{ year: number | null, words: string[] }}
     */
    const parseVehicleQuery = (q) => {
        const tokens = (q || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
        let year = null;
        const words = [];
        for (const t of tokens) {
            if (year === null && isYear(t)) year = +t;
            else words.push(t);
        }
        return { year, words };
    };

    /**
     * Allows the user to search by Make, Model, VIN, License Plate, 
     * and Comments field.
     */
    const filteredRows = useMemo(() => {
        const q = (searchTerm || '').trim().toLowerCase();
        if (!q) return vehicles;

        const { year, words } = parseVehicleQuery(q);

        return vehicles.filter((row) => {
            const make = (row.Make || '').toLowerCase();
            const model = (row.Model || '').toLowerCase();
            const makeModel = (make + ' ' + model).trim();
            const comments = (displayComments(row) || '').toLowerCase();

            const ahp = row.OtherId != null ? String(row.OtherId).toLowerCase() : "";
            const dashcam = (row.DashcamSerial || "").toLowerCase();
            const sms = (row.SMS || "").toLowerCase();
            
            const vinMatch = (row.VIN || '').toLowerCase().includes(q);
            const plateMatch = (row.License || '').toLowerCase().includes(q);
            const commentsMatch = comments.includes(q);

            const ahpMatch = ahp.includes(q);
            const dashcamMatch = dashcam.includes(q);
            const smsMatch = sms.includes(q);

            if (vinMatch || plateMatch || commentsMatch || ahpMatch || dashcamMatch || smsMatch) {
                return true;
            }

            if (year !== null && Number(row.Year) !== year) return false;

            const allWordsMatch = words.every(w => makeModel.includes(w));

            return words.length ? allWordsMatch : (year !== null);
        });
    }, [vehicles, searchTerm]);

    useEffect(() => {
        setRowSelectionModel(prev => 
            prev.filter(id => filteredRows.some(r => (r.VehicleId ?? r.id) === id))
        );
    }, [filteredRows]);

    const cardPageCount = Math.max(1, Math.ceil(filteredRows.length / cardPageSize ));
    const pagedMobileRows = useMemo(() => {
        const start = (cardPage - 1) * cardPageSize;
        return filteredRows.slice(start, start + cardPageSize);
    }, [filteredRows, cardPage, cardPageSize]);

    useEffect(() => {
        setCardPage((p) => Math.min(p, Math.ceil(filteredRows.length / cardPageSize) || 1));
    }, [filteredRows, cardPageSize]);

    /**
     * Fetches vehicles from the API with optional query and archived filter.
     * Populates the "vehicles" state with normalized rows including an "id" field.
     * 
     * @param {string} [q='']
     * @param {'0' | '1' | 'all'} [archived=archivedFilter]
     */
    const fetchVehicles = useCallback(async (q = '', archived = archivedFilter) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_BASE}/api/vehicles`, { 
                params: { q, archived }, 
                headers: {
                    Authorization: `Bearer ${token}`,
                }
             });
            setVehicles((data?.data || []).map(r => ({ id: r.VehicleId, ...r })));
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [archivedFilter, token]);

    useEffect(() => {
        fetchVehicles('', archivedFilter);
        setRowSelectionModel([]);
    }, [archivedFilter, fetchVehicles]);
    
    /**
     * Fetches service history for a given vehicle and stores it in "history".
     * 
     * @param {number | string } vehicleId
     */
    const fetchHistory = useCallback(async (vehicleId) => {
        setHistoryLoading(true);
        try {
            const { data } = await axios.get(
                `${API_BASE}/api/service-history/${vehicleId}/history`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );
            setHistory((data?.data || []).map(r => ({ id: r.ServiceId, ...r })));
        } catch (error) {
            console.error(error);
        } finally {
            setHistoryLoading(false);
        }
    }, [token]);

    useEffect(() => { 
        if (!loadingUser && token) {
            fetchVehicles(); 
        }
    }, [fetchVehicles, loadingUser, token]);

    useEffect(() => {
        if (!selectedVehicle) {
            return;
        }
        const fresh = vehicles.find(v => v.VehicleId === selectedVehicle.VehicleId);
        if (fresh) {
            setSelectedVehicle(fresh);
        }
    }, [vehicles]);     // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Ceates or updates a vehicle by calling the relevant API endpoint.
     * On success, it shows the appropriate notification and refreshes the vehicle list.
     *
     * @param {Object} v
     * @returns {Promise<boolean>} Whether the save succeeded.
     */
    const handleSaveVehicle = async (v) => {
        try {
            if (v.VehicleId) {
                await axios.put(`${API_BASE}/api/vehicles/${v.VehicleId}`, v,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    }
                );
                toast.success('Vehicle updated successfully!');
            } else {
                await axios.post(`${API_BASE}/api/vehicles`, v, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                });
                toast.success('Vehicle created successfully!');
            }
            fetchVehicles();
            return true;
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.error || 'Failed to save vehicle');
            return false;
        }
    };

    /**
     * Deletes a vehicle by id and refreshes the vehicle list.
     * Also closes the side drawer and shows a success message.
     * 
     * @param {number | string} id 
     */
    const handleDeleteVehicle = async (id) => {
        try {
            await axios.delete(`${API_BASE}/api/vehicles/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    }
                }
            );
            fetchVehicles();
            setOpenDrawer(false);
            toast.success('Vehicle deleted successfully!');
        } catch (error) {
            console.error(error);
            toast.error('Failed to delete vehicle');
        }
    };

    /**
     * Creates or updates a service record, applying "Safety Inspection" normalization first.
     * 
     * On success, refreshes the vehicle's history and shows a success message.
     * 
     * @param {Object} payload 
     */
    const handleSaveService = async (payload) => {
        try {
            const body = payload;
            
            if (payload.ServiceId) {
                await axios.put(
                    `${API_BASE}/api/service-history/${payload.ServiceId}`,
                    body,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    }

                );
                toast.success('Service record updated sucessfully');
            } else {
                await axios.post(
                    `${API_BASE}/api/vehicles/${selectedVehicle.VehicleId}/history`,
                    body,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        }
                    }
                );
                toast.success('Service record created successfully');
            }
            fetchHistory(selectedVehicle.VehicleId);
        } catch (error) {
            console.error(error);
            toast.error('Failed to save service record');
        }
    };

    /**
     * Deletes a service record by id and refreshes history.
     * 
     * @param {number | string} serviceId 
     */
    const handleDeleteService = async (serviceId) => {
        try {
            await axios.delete(`${API_BASE}/api/service-history/${serviceId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });
            fetchHistory(selectedVehicle.VehicleId);
            toast.success('Service record deleted successfully');
        } catch (error) {
            console.error(error);
            toast.error('Failed to delete service record');
        }
    };

    /**
     * Opens a generic confirmation dialog with the given configuration.
     * 
     * @param {{ title: string, content: string, onConfirm: () => Promise<void> | void }} param0
     */
    const ask = ({ title, content, onConfirm }) => setConfirm({ open: true, title, content, loading: false, onConfirm });

    const runConfirm = async () => {
        if (!confirm.onConfirm) {
            setConfirm((s) => ({ ...s, open: false }));
            return;
        }
        try {
            setConfirm((s) => ({ ...s, loading: true }));
            await confirm.onConfirm();
        } finally {
            setConfirm({ open: false, title: '', content: '', loading: false, onConfirm: null });
        }
    };

    /**
     * Shows a confirmation dialog before deleting a vehicle.
     * 
     * @param {Object} vehicle 
     */
    const confirmDeleteVehicle = (vehicle) => {
        ask({
            title: "Delete vehicle?",
            content: `This will permanently remove ${vehicle.Year} ${vehicle.Make} ${vehicle.Model}.`,
            onConfirm: () => handleDeleteVehicle(vehicle.VehicleId),
        });
    };

    /**
     * Shows a confirmation dialog before deleting a service record.
     * 
     * @param {Object} row 
     */
    const confirmDeleteService = (row) => {
        ask({
            title: 'Delete service record?',
            content: `Delete the record dated ${row.ServiceDate}? This cannot be undone.`,
            onConfirm: () => handleDeleteService(row.ServiceId),
        });
    };

    /**
     * Generic confirmation dialog used for destructive actions like delete.
     * 
     * @param {Object} props
     * @param {boolean} props.open
     * @param {string} props.title
     * @param {string} props.content
     * @param {string} [props.confirmText]
     * @param {boolean} [props.loading]
     * @param {() => void} props.onCancel
     * @param {() => void} props.onConfirm
     * @returns {JSX.Element}
     */
    const ConfirmDialog = ({ open, title, content, confirmText = 'Delete', loading = false, onCancel, onConfirm }) => {
        return (
            <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">{content}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancel} disabled={loading}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={onConfirm} disabled={loading}>
                        {confirmText}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    /**
     * Archives all currently selected vehicles in parallel.
     * Clears the selection, closes the bulk dialog, and refreshes the grid on success.
     */
    const handleBulkArchive = async () => {
        const ids = rowSelectionModel;
        if (!ids.length) return;

        try {
            const headers = { headers: { Authorization: `Bearer ${token}` } };

            await Promise.all(
                ids.map((id) =>
                    axios.post(`${API_BASE}/api/vehicles/${id}/archive`, {}, headers)
                )
            );

            toast.success(`Archived ${ids.length} vehicle${ids.length > 1 ? 's' : ''}`);
            setBulkArchiveOpen(false);
            setRowSelectionModel([]);
            fetchVehicles('', archivedFilter);
        } catch (e) {
            console.error(e);
            toast.error('Failed to archive some vehicles');
        }
    };

    /**
     * Unarchives all currently selected vehicles in parallel.
     * Clears the selection and refreshes the grid on success.
     */
    const handleBulkUnarchive = async () => {
        const ids = rowSelectionModel;
        if (!ids.length) return;

        try {
            const headers = { headers: { Authorization: `Bearer ${token}` } };
            await Promise.all(
            ids.map((id) =>
                axios.post(`${API_BASE}/api/vehicles/${id}/unarchive`, {}, headers)
            )
            );

            toast.success(`Unarchived ${ids.length} vehicle${ids.length > 1 ? 's' : ''}`);
            setRowSelectionModel([]);
            fetchVehicles('', archivedFilter);
        } catch (e) {
            console.error(e);
            toast.error('Failed to unarchive some vehicles');
        }
    };

    /**
     * Handles row click from the grid/card, opening the drawer and loading history.
     * @param {{ row: Object }} params 
     */
    const onRowClick = async (params) => {
        setSelectedVehicle(params.row);
        setOpenDrawer(true);
        await fetchHistory(params.row.VehicleId || params.row.id);
    };

    /**
     * Toggles selection for a single vehicle id.
     * 
     * @param {number | string} id 
     */
    const toggleSelect = (id) => {
        setRowSelectionModel((m) => 
            m.includes(id) ? m.filter((x) => x !== id) : [...m, id]
        );
    };
    
    /**
     * Toggles selection of all rows on the current mobile page.
     */
    const toggleSelectAllPage = () => {
        setRowSelectionModel(prev => {
            if (isAllPageSelected) {
                const toKeep = new Set(prev);
                pageIds.forEach(id => toKeep.delete(id));
                return Array.from(toKeep);
            }
            return Array.from(new Set([...prev, ...pageIds]));
        });
    };

    /**
     * Selects all filtered rows across all pages.
     */
    const selectAllFiltered = () => {
        setRowSelectionModel(allFilteredIds);
    };

    /**
     * Clears the current row selection.
     */
    const clearSelection = () => setRowSelectionModel([]);

    const pageIds = useMemo(
        () => pagedMobileRows.map(r => r.VehicleId ?? r.id),
        [pagedMobileRows]
    );

    const allFilteredIds = useMemo(
        () => filteredRows.map(r => r.VehicleId ?? r.id),
        [filteredRows]
    );

    const isAllPageSelected = useMemo(
        () => pageIds.length > 0 && pageIds.every(id => rowSelectionModel.includes(id)),
        [pageIds, rowSelectionModel]
    );

    const isSomePageSelected = useMemo(
        () => pageIds.some(id => rowSelectionModel.includes(id)) && !isAllPageSelected,
        [pageIds, rowSelectionModel, isAllPageSelected]
    );

    /**
     * Mobile-friendly vehicle card component with selection checkbox.
     * 
     * @param {Object} props 
     * @param {Object} props.row
     * @param {() => void} props.onClick
     * @param {boolean} props.selected
     * @param {(id: number | string) => void} props.onToggle
     * @returns {JSX.Element} 
     */
    const MobileCard = ({ row, onClick, selected, onToggle }) => (
        <Card
            variant="outlined"
            sx={{
            mb: 1.5,
            borderRadius: 2,
            ...(selected
                ? { borderColor: 'primary.main', boxShadow: 2 }
                : null),
            }}
            onClick={onClick}
        >
            <CardContent sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Checkbox
                checked={selected}
                size="small"
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggle(row.VehicleId ?? row.id)}
                sx={{ mt: 0.25 }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                    {row.Year} {row.Make} {row.Model}
                </Typography>

                <Box
                    sx={{
                    mt: 0.75,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.25,
                    }}
                >
                    <Box>
                    <Typography variant="caption" color="text.secondary">
                        VIN
                    </Typography>
                    <Typography variant="body2" noWrap title={row.VIN}>
                        {row.VIN}
                    </Typography>
                    </Box>

                    <Box>
                    <Typography variant="caption" color="text.secondary">
                        License Plate
                    </Typography>
                    <Typography variant="body2" noWrap title={row.License}>
                        {row.License}
                    </Typography>
                    </Box>

                    {row.OtherId && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                        AHP
                        </Typography>
                        <Typography variant="body2" noWrap title={row.OtherId}>
                        {row.OtherId}
                        </Typography>
                    </Box>
                    )}
                </Box>

                {row.Comments ? (
                    <Typography
                    variant="body2"
                    sx={{ mt: 1, whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                    <Box component="span" fontWeight={600}>
                        Comments:{' '}
                    </Box>
                    {row.Comments}
                    </Typography>
                ) : null}
                </Box>
            </Box>
            </CardContent>
        </Card>
    );

    /**
     * Mobile-friendly service history card for the selected vehicle.
     * 
     * @param {{ row: Object }} props 
     * @returns {JSX.Element}
     */
    const HistoryCard = ({ row }) => (
        <Card variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
            <CardContent sx={{ p: 1.5 }}>
                <Stack spacing={0.5}>
                    <Typography variant="subtitle2">
                        <Box component="span" fontWeight={600}>Service Date:</Box> {row.ServiceDate}
                    </Typography>
                    <Typography variant="body2">
                        <Box component="span" fontWeight={600}>Description:</Box> {row.Description}
                    </Typography>
                    <Typography variant="body2">
                        <Box component="span" fontWeight={600}>Mileage:</Box> {row.Mileage} km
                    </Typography>
                    <Typography variant="body2">
                        <Box component="span" fontWeight={600}>Provider:</Box> {row.Provider}
                    </Typography>
                    <Typography variant="body2">
                        <Box component="span" fontWeight={600}>Total:</Box> ${row.TotalCost}
                    </Typography>
                    {row.Notes ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            <Box component="span" fontWeight={600}>Notes:</Box> {row.Notes}
                        </Typography>
                    ) : null}
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Container maxWidth="lg" className="dashboard-container">
            <Box
                sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                }}
            >
                <img
                    src="/images/rct-logo.png"
                    alt="Rapid City Transportation Logo"
                    style={{ height: 52, width: 'auto' }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {user?.email && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {user.email}
                        </Typography>
                    )}
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={logout}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            borderColor: 'grey.400',
                            color: 'grey.700',
                            '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
                        }}
                    >
                        Log Out
                    </Button>
                </Box>
            </Box>

            <Box sx={{ display: { xs: 'block', sm: 'none' }, borderBottom: '1px solid', borderColor: 'grey.200', pb: 2.5, pt: 0.5 }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 2,
                    }}
                >
                    {user?.email && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {user.email}
                        </Typography>
                    )}
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={logout}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            flexShrink: 0,
                            borderColor: 'grey.400',
                            color: 'grey.700',
                            '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
                        }}
                    >
                        Log Out
                    </Button>
                </Box>
                {/* Centered logo */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <img
                        src="/images/rct-logo.png"
                        alt="Rapid City Transportation Logo"
                        style={{ height: 36, width: 'auto' }}
                    />
                </Box>
            </Box>

            {/* Due U10 safety inspections - self-hides when nothing's due */}
            <Box sx={{ mt: { xs: 2, sm: 2 } }}>
                <U10RemindersPanel API_BASE={API_BASE} token={token} />
            </Box>

            {/* ── Toolbar: Search + Actions ── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 1.5 },
                    mt: { xs: 3, sm: 2 },
                    mb: { xs: 2.5, sm: 2 },
                }}
            >
                <TextField
                    placeholder="Search vehicles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    className="dashboard-search"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ flexGrow: 1, order: { xs: 1, sm: 0 } }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, alignSelf: { xs: 'center', sm: 'center' }, order: { xs: 0, sm: 1 } }}>
                    {isAdmin && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<StoreIcon />}
                            onClick={() => setProvidersOpen(true)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                bgcolor: 'grey.800',
                                '&:hover': { bgcolor: 'grey.900' },
                                height: 36.5,
                            }}
                        >
                            Providers
                        </Button>
                    )}
                    <ReportsMenu API_BASE={API_BASE} token={token} vehicles={vehicles} />
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: { xs: 1.5, sm: 1 },
                    mb: { xs: 1.5, sm: 1.5 },
                }}
            >
                <Tabs
                    value={archivedFilter}
                    onChange={(_, v) => setArchivedFilter(v)}
                    sx={{ minHeight: 44 }}
                >
                    <Tab value="0" label="Active" />
                    <Tab value="1" label="Archived" />
                    <Tab value="all" label="All" />
                </Tabs>

                {isAdmin && archivedFilter !== 'all' && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {archivedFilter === '0' ? (
                            <Button
                                variant="contained"
                                color="warning"
                                size="small"
                                disabled={!selectedCount}
                                onClick={() => setBulkArchiveOpen(true)}
                            >
                                Archive Selected ({selectedCount})
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="success"
                                size="small"
                                disabled={!selectedCount}
                                onClick={handleBulkUnarchive}
                            >
                                Unarchive Selected ({selectedCount})
                            </Button>
                        )}
                        {selectedCount > 0 && (
                            <Button size="small" onClick={() => setRowSelectionModel([])}>Clear</Button>
                        )}
                    </Box>
                )}
            </Box>
            
            {isMobile && (
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                        px: 1,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center', 
                        gap: 1,
                    }}
                >
                    <Checkbox
                        size="small"
                        checked={isAllPageSelected}
                        indeterminate={isSomePageSelected}
                        onChange={toggleSelectAllPage}
                        inputProps={{ 'aria-label': 'Select all items on this page' }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                        Select page ({pageIds.length}) - Selected: {rowSelectionModel.length}/{allFilteredIds.length}
                    </Typography>
                    <Button
                        size="small"
                        onClick={selectAllFiltered}
                        disabled={allFilteredIds.length === 0 || rowSelectionModel.length === allFilteredIds.length}
                    >
                        Select All ({allFilteredIds.length})
                    </Button>
                    {rowSelectionModel.length > 0 && (
                        <Button size="small" onClick={clearSelection}>
                            Clear
                        </Button>
                    )}
                </Box>
            )}

            {isMobile ? (
                <Box sx={{ mt: 1.5 }}>
                    {pagedMobileRows.map((r) => {
                        const id = r.VehicleId ?? r.id;
                        return (
                            <MobileCard 
                                key={r.VehicleId ?? r.id} 
                                row={r} 
                                onClick={() => onRowClick({row: r })} 
                                selected={rowSelectionModel.includes(id)}
                                onToggle={toggleSelect}
                            />
                        );
                    })}

                    <Box display = "flex" alignItems="center" justifyContent="space-between" mt={1.5}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">Rows per page:</Typography>
                            <Select
                                size="small"
                                value={cardPageSize}
                                onChange={(e) => { 
                                    setCardPageSize(e.target.value);
                                    setCardPage(1);
                                }}
                            >
                                {[5, 10, 25].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                            </Select>
                        </Box>
                        
                        <Pagination
                            page={cardPage}
                            count={cardPageCount}
                            onChange={(_, p) => setCardPage(p)}
                            color="primary"
                            size="small"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </Box>
            ) : (
                <Box className="dashboard-table">

                    <DataGrid
                        autoHeight
                        rows={filteredRows}
                        columns={COLUMNS}
                        getRowId={(row) => row.VehicleId ?? row.id}
                        loading={loading}
                        pageSize={pageSize}
                        onPageSizeChange={(newSize) => setPageSize(newSize)}
                        rowsPerPageOptions={[10, 25, 50]}
                        onRowClick={onRowClick}
                        checkboxSelection
                        disableSelectionOnClick
                        selectionModel={rowSelectionModel}
                        onSelectionModelChange={(m) => setRowSelectionModel(m)}
                        sx={{
                            '& .MuiDataGrid-cell': { alignItems: 'flex-start' },
                            '& .MuiDataGrid-cellContent': {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: 1.3,
                                overflow: 'visible',
                                textOverflow: 'unset',
                            },
                            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
                        }}
                    />
                </Box>
            )}

            <Drawer 
                 anchor="right"
                 open={openDrawer} 
                 onClose={() => setOpenDrawer(false)} 
                 PaperProps={{ 
                    sx: { 
                        width: { xs: '100%', sm: 720, md: 960 },
                        height: { xs: '100%', sm: 'auto' },
                        maxWidth: '100vw',
                        borderRadius: { xs: 0, sm: 2 },
                        p: 0,
                        display: 'flex', 
                        flexDirection: 'column',
                    }
                }}
            >
                <Box 
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                        px: 2,
                        py: 1.5
                    }}
                >
                    <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6" gutterBottom>
                            {selectedVehicle ? `${selectedVehicle.Year} ${selectedVehicle.Make} ${selectedVehicle.Model}`: 'Vehicle'}
                        </Typography>

                        {isAdmin && selectedVehicle && (
                            <Box>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setDlgInitial(selectedVehicle);
                                        setDlgOpen(true);
                                    }}
                                >
                                    Edit
                                </Button>
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => confirmDeleteVehicle(selectedVehicle)}
                                >
                                    Delete
                                </Button>
                            </Box>
                        )}
                        <IconButton edge="end" onClick={() => setOpenDrawer(false)} aria-label="Close">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    
                    <Box sx={{ px: 3 }}>
                        <Typography variant="body2">
                            <Box component="span" fontWeight={600}>VIN:</Box> {selectedVehicle?.VIN} 
                        </Typography>
                        <Typography variant="body2">
                            <Box component="span" fontWeight={600}>License Plate:</Box> {selectedVehicle?.License}
                        </Typography>
                        {selectedVehicle?.DashcamSerial ? (
                            <Typography variant="body2">
                                <Box component="span" fontWeight={600}>Dashcam Serial Number:</Box> {selectedVehicle.DashcamSerial}
                            </Typography>
                        ) : null}

                        {selectedVehicle?.SMS ? (
                            <Typography variant="body2">
                                <Box component="span" fontWeight={600}>SMS (Satellite Monitoring System):</Box> {selectedVehicle.SMS}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>

                {historyLoading ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height={200}><CircularProgress /></Box>
                ) : isMobile ? (
                        <Box margin={3}>
                            {history.map((h) => (
                                <HistoryCard key={h.id ?? h.ServiceId} row={h} />
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ height: 520, margin: 3 }}>
                            <DataGrid 
                                rows={history} 
                                columns={historyCols} 
                                pageSize={10} 
                                rowsPerPageOptions={[10, 25]} 
                                sx={{
                                    '& .MuiDataGrid-cell': { alignItems: 'flex-start' },
                                    '& .MuiDataGrid-cellContent': {
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        lineHeight: 1.3,
                                        overflow: 'visible',
                                        textOverflow: 'unset',
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {isAdmin && selectedVehicle && (
                        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    setServiceDlgInit(null);
                                    setServiceDlgOpen(true);
                                }}
                            >
                                Add Service Record
                            </Button>
                        </Box>
                    )}
            </Drawer>

            {isAdmin && (
                <>
                    <Tooltip
                        title="Add vehicle"
                        arrow
                        enterDelay={400}
                        enterTouchDelay={0}
                    >
                        <IconButton
                            sx={{
                                position: 'fixed',
                                bottom: 24,
                                right: 24,
                                bgcolor: 'primary.main',
                                '&:hover': { bgcolor: 'primary.dark' }
                            }}
                            onClick={() => {
                                setDlgInitial(null);
                                setDlgOpen(true);
                            }}
                        >
                            <AddIcon htmlColor="#fff" />
                        </IconButton>
                    </Tooltip>

                    <VehicleDialog
                        open={dlgOpen}
                        onClose={() => setDlgOpen(false)}
                        initial={dlgInitial}
                        onSave={handleSaveVehicle}
                    />

                    <ServiceDialog
                        open={serviceDlgOpen}
                        onClose={() => setServiceDlgOpen(false)}
                        initial={serviceDlgInit}
                        onSave={handleSaveService}
                    />

                    <ConfirmDialog 
                        open={confirm.open}
                        title={confirm.title}
                        content={confirm.content}
                        loading={confirm.loading}
                        onCancel={() => setConfirm((s) => ({ ...s, open: false }))}
                        onConfirm={runConfirm}
                    />

                    <BulkArchiveDialog
                        open={bulkArchiveOpen}
                        onClose={() => setBulkArchiveOpen(false)}
                        onSubmit={handleBulkArchive}
                        count={selectedCount}
                    />

                    {isAdmin && (
                        <ProvidersDialog
                            open={providersOpen}
                            onClose={() => setProvidersOpen(false)}
                            API_BASE={API_BASE}
                            token={token}
                        />
                    )}
                </>
            )}
            <Dialog
                open={noteViewer.open}
                onClose={() => setNoteViewer({ open: false, text: '' })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Notes</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {noteViewer.text}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNoteViewer({ open: false, text: '' })}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default FleetDashboard;
